export type SalesDay = { date: string; orders: number; grossCents: number; posCents: number; onlineCents: number };
export type ShopifySummary = {
  sinceDate: string; fetchedAt: string; currency: string | null; orderCount: number; grossCents: number;
  truncated: boolean; itemsTruncated?: boolean; days: SalesDay[];
  itemDays: Array<{ businessDate: string; items: Record<string, { quantity: number; grossCents: number }> }>;
};
export type ShopifyCurrencySales = Pick<ShopifySummary, 'currency' | 'orderCount' | 'grossCents' | 'days' | 'itemDays'>;
export type ShopifyMultiCurrencySummary = Pick<ShopifySummary, 'sinceDate' | 'fetchedAt' | 'truncated' | 'itemsTruncated'> & {
  currencyBasis: 'customer'; storeCurrency: string | null; orderCount: number;
  currencies: ShopifyCurrencySales[];
};

// Old API responses can contain a blended total. Never use that total as a
// fallback while deployments roll out or a previously cached response remains.
export function selectSalesCurrency(summary?: ShopifyMultiCurrencySummary, preferred?: string): ShopifySummary | undefined {
  if (summary?.currencyBasis !== 'customer' || !Array.isArray(summary.currencies)) return undefined;
  const currency = preferred || [...summary.currencies].sort((a, b) => b.orderCount - a.orderCount || String(a.currency).localeCompare(String(b.currency)))[0]?.currency || summary.storeCurrency;
  if (!currency) return undefined;
  const group = summary.currencies.find(group => group.currency === currency);
  return { ...summary, currency, orderCount: group?.orderCount || 0, grossCents: group?.grossCents || 0, days: group?.days || [], itemDays: group?.itemDays || [] };
}

export function salesCsv(summary: ShopifySummary) {
  const rows = [['Date (UTC)', `Orders (${summary.currency})`, `Shopify order value (${summary.currency})`, 'Coverage'],
    ...salesTimeline(summary).map(day => [day.date, day.orders ?? '', day.grossCents == null ? '' : (day.grossCents / 100).toFixed(2), summary.truncated ? 'Partial' : 'Complete'])];
  return rows.map(row => row.join(',')).join('\r\n');
}
export type OperatingOrder = {
  id: string; source: string; externalRef: string | null; customerName: string | null;
  destination: string | null; deliveryAddress?: string | null; deliveryStatus: string;
  fulfillmentDate: string | null; notes?: string | null;
  lines: Array<{ sourceName: string; quantity: string | number }>;
};

// Only fill gaps inside a successfully fetched window. A truncated response
// cannot establish that a missing day had no orders, so leave those gaps null.
export function salesTimeline(summary: ShopifySummary): Array<{ date: string; orders: number | null; grossCents: number | null; posCents: number | null; onlineCents: number | null }> {
  const start = Date.parse(`${summary.sinceDate}T00:00:00Z`);
  const end = Date.parse(`${summary.fetchedAt.slice(0, 10)}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return summary.days;
  const days = new Map(summary.days.map(day => [day.date, day]));
  return Array.from({ length: Math.min(31, Math.round((end - start) / 86400000) + 1) }, (_, i) => {
    const date = new Date(start + i * 86400000).toISOString().slice(0, 10);
    const missing = summary.truncated ? null : 0;
    return days.get(date) || { date, orders: missing, grossCents: missing, posCents: missing, onlineCents: missing };
  });
}

export function rankedProducts(summary: ShopifySummary) {
  const products = new Map<string, { name: string; quantity: number; grossCents: number }>();
  for (const day of summary.itemDays || []) for (const [name, item] of Object.entries(day.items)) {
    const product = products.get(name) || { name, quantity: 0, grossCents: 0 };
    product.quantity += item.quantity;
    product.grossCents += item.grossCents;
    products.set(name, product);
  }
  return [...products.values()].sort((a, b) => b.quantity - a.quantity || a.name.localeCompare(b.name));
}

export function openShopifyOrders(orders: OperatingOrder[]) {
  return orders.filter(order => order.source === 'shopify' && order.deliveryStatus !== 'delivered');
}
export function orderUnits(order: OperatingOrder) {
  return order.lines.reduce((total, line) => total + (Number(line.quantity) || 0), 0);
}
export function orderReference(order: OperatingOrder) {
  // The stored reference is a Shopify ID, not a human order number.
  return `Shopify ID …${(order.externalRef || order.id).split('/').pop()?.slice(-6)}`;
}
